import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faEdit, faTrash, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { useCampaigns } from '../hooks/useCampaigns';
import { useRateCards, RateCardForm, RateCardList } from '../../rateCards';
import { useFxRates } from '../../../shared/hooks/useFxRates';
import CampaignForm from './CampaignForm';
import { Campaign, CreateCampaignRequest } from '../../../shared/types/campaign';
import { useSearch } from '../../search/hooks/useSearch';
import StaffProfileCard from '../../search/components/StaffProfileCard';
import { SelectedCampaignProfile } from '../../../shared/types/campaign';
import { RateCard } from '../../../shared/types/rateCard';

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'campaigns' | 'ratecards'>('campaigns');

  const {
    campaigns,
    loading,
    error,
    createCampaign,
    updateCampaign,
    deleteCampaign,
  } = useCampaigns();

  const {
    rateCards,
    loading: rateCardsLoading,
    error: rateCardsError,
    createRateCard,
    updateRateCard,
    deleteRateCard,
  } = useRateCards();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | undefined>();
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | undefined>();
  const [formLoading, setFormLoading] = useState(false);

  // Rate card management state
  const [isRateCardFormOpen, setIsRateCardFormOpen] = useState(false);
  const [editingRateCard, setEditingRateCard] = useState<RateCard | undefined>();
  const [rateCardFormLoading, setRateCardFormLoading] = useState(false);
  const [draftCampaign, setDraftCampaign] = useState<CreateCampaignRequest | null>(null);
  const [selectedProfiles, setSelectedProfiles] = useState<SelectedCampaignProfile[]>([]);
  const [convertedRates, setConvertedRates] = useState<Record<string, number>>({});

  // Search hook for live profile preview
  const {
    profiles: previewProfiles,
    loading: previewLoading,
    fetchProfiles: fetchPreviewProfiles,
    clearAll: clearPreview,
  } = useSearch();

  // FX rates
  const { convert, roundToNearestFive } = useFxRates();

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'BRL': return 'R$';
      default: return '$';
    }
  };

  const selectedRateCard = rateCards.find(rc => rc.id === (draftCampaign?.rateCardId || selectedCampaign?.rateCardId));

  // Update converted rates when currency or rate card changes
  useEffect(() => {
    const updateRates = async () => {
      if (!selectedRateCard) {
        setConvertedRates({});
        return;
      }

      const campaignCurrency = (draftCampaign?.currency || selectedCampaign?.currency || 'USD') as any;
      const rcCurrency = selectedRateCard.currency as any;
      const convertedRates: Record<string, number> = {};

      // Convert rates for each role in the rate card
      for (const role of selectedRateCard.roles) {
        if (campaignCurrency === rcCurrency) {
          // No conversion needed, but still round to nearest 5
          convertedRates[role.id] = roundToNearestFive(role.dailyRate);
        } else {
          try {
            const converted = await convert(role.dailyRate, rcCurrency, campaignCurrency);
            convertedRates[role.id] = roundToNearestFive(converted);
          } catch (error) {
            console.error('Currency conversion failed for role:', role.title, error);
            // Fallback to original rate, still rounded
            convertedRates[role.id] = roundToNearestFive(role.dailyRate);
          }
        }
      }

      setConvertedRates(convertedRates);
    };

    updateRates();
  }, [selectedRateCard, draftCampaign?.currency, selectedCampaign?.currency, convert, roundToNearestFive]);

  const handleCreateCampaign = async (campaignData: CreateCampaignRequest) => {
    setFormLoading(true);
    try {
      await createCampaign({
        ...campaignData,
        selectedProfiles,
      });
      setIsFormOpen(false);
      setDraftCampaign(null);
      setSelectedProfiles([]);
      clearPreview();
    } catch (error) {
      console.error('Error creating campaign:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateCampaign = async (campaignData: CreateCampaignRequest) => {
    if (!editingCampaign) return;

    setFormLoading(true);
    try {
      await updateCampaign({
        ...campaignData,
        id: editingCampaign.id,
        selectedProfiles,
      });
      setIsFormOpen(false);
      setEditingCampaign(undefined);
    } catch (error) {
      console.error('Error updating campaign:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setIsFormOpen(true);
  };

  const handleSelectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setEditingCampaign(campaign);
    setIsFormOpen(true);
    setSelectedProfiles(campaign.selectedProfiles || []);
    setDraftCampaign({
      title: campaign.title,
      description: campaign.description || '',
      shortUrl: campaign.shortUrl,
      profileFilters: campaign.profileFilters,
      currency: campaign.currency,
      selectedProfiles: campaign.selectedProfiles || [],
      isActive: campaign.isActive,
    });
  };

  const handleDeleteCampaign = async (campaign: Campaign) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      await deleteCampaign(campaign);
    }
  };

  const handleToggleActive = async (campaign: Campaign) => {
    await updateCampaign({
      ...campaign,
      id: campaign.id,
      isActive: !campaign.isActive,
    });
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingCampaign(undefined);
    setDraftCampaign(null);
    setSelectedProfiles([]);
    clearPreview();
  };

  // Memoize the preview update function to prevent infinite re-renders
  const updatePreview = useCallback(() => {
    if (!draftCampaign) return;
    const filters = {
      selectedRoles: draftCampaign.profileFilters.roles || [],
      selectedSkills: draftCampaign.profileFilters.skills || [],
      rolesSearch: draftCampaign.profileFilters.rolesSearch || '',
      skillsSearch: draftCampaign.profileFilters.skillsSearch || '',
      industryFilter: '',
    };
    fetchPreviewProfiles(filters, true);
  }, [
    draftCampaign?.profileFilters.roles,
    draftCampaign?.profileFilters.skills,
    draftCampaign?.profileFilters.rolesSearch,
    draftCampaign?.profileFilters.skillsSearch,
    fetchPreviewProfiles
  ]);

  // Update preview only when filters change (not currency or other fields)
  useEffect(() => {
    updatePreview();
  }, [updatePreview]);

  const handleDraftChange = useCallback((data: CreateCampaignRequest) => {
    setDraftCampaign({
      ...data,
      selectedProfiles,
      currency: data.currency,
    });
  }, [selectedProfiles]);

  const toggleSelectProfile = (profile: any) => {
    setSelectedProfiles(prev => {
      const existing = prev.find(p => p.profileId === profile.id);
      if (existing) {
        return prev.filter(p => p.profileId !== profile.id);
      }
      // When adding a profile, we need to require role selection
      // For now, we'll add it without a role and let the user select one in the form
      return [...prev, { profileId: profile.id, selectedRoleId: '' }];
    });
  };

  // no-op: per-profile dayRate removed; kept for compatibility if referenced
  const updateSelectedDayRate = (_profileId: string, _value: number) => { };

  // Rate card handlers
  const handleCreateRateCard = async (rateCardData: any) => {
    setRateCardFormLoading(true);
    try {
      await createRateCard(rateCardData);
      setIsRateCardFormOpen(false);
      setEditingRateCard(undefined);
    } catch (error) {
      console.error('Error creating rate card:', error);
    } finally {
      setRateCardFormLoading(false);
    }
  };

  const handleUpdateRateCard = async (rateCardData: any) => {
    if (!editingRateCard) return;

    setRateCardFormLoading(true);
    try {
      await updateRateCard({ ...rateCardData, id: editingRateCard.id });
      setIsRateCardFormOpen(false);
      setEditingRateCard(undefined);
    } catch (error) {
      console.error('Error updating rate card:', error);
    } finally {
      setRateCardFormLoading(false);
    }
  };

  const handleEditRateCard = (rateCard: RateCard) => {
    setEditingRateCard(rateCard);
    setIsRateCardFormOpen(true);
  };

  const handleDeleteRateCard = async (rateCard: RateCard) => {
    if (window.confirm(`Are you sure you want to delete "${rateCard.title}"?`)) {
      await deleteRateCard(rateCard);
    }
  };

  const handleCreateNewRateCard = () => {
    setEditingRateCard(undefined);
    setIsRateCardFormOpen(true);
  };

  const handleCloseRateCardForm = () => {
    setIsRateCardFormOpen(false);
    setEditingRateCard(undefined);
  };


  return (
    <div className="min-h-screen bg-gray-50 flex w-full">
      {/* Left Panel - Tab Navigation */}
      <div className="w-1/4 bg-white border-r border-gray-200 flex flex-col">
        {/* Tab Navigation */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'campaigns'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Campaigns
            </button>
            <button
              onClick={() => setActiveTab('ratecards')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'ratecards'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Rate Cards
            </button>
          </div>
        </div>

        {/* Campaign Tab Content */}
        {activeTab === 'campaigns' && (
          <>
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">Campaigns</h1>
                <button
                  onClick={() => setIsFormOpen(true)}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  New
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Campaign List */}
            <div className="flex-1 overflow-y-auto">
              {error && (
                <div className="m-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              {loading ? (
                <div className="p-6 text-center">
                  <FontAwesomeIcon icon={faSpinner} className="text-2xl text-blue-600 animate-spin mb-4" />
                  <p className="text-gray-600 text-sm">Loading campaigns...</p>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-500 text-sm">No campaigns found</p>
                  <button
                    onClick={() => setIsFormOpen(true)}
                    className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Create your first campaign
                  </button>
                </div>
              ) : (
                <div className="p-2">
                  {campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className={`p-4 mb-2 rounded-lg cursor-pointer transition-colors ${selectedCampaign?.id === campaign.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      onClick={() => handleSelectCampaign(campaign)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {campaign.title}
                            </h3>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${campaign.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                                }`}
                            >
                              {campaign.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                            {campaign.description}
                          </p>
                        </div>

                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleActive(campaign);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title={campaign.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <FontAwesomeIcon
                              icon={campaign.isActive ? faToggleOn : faToggleOff}
                              className={`text-sm ${campaign.isActive ? 'text-green-500' : 'text-gray-400'}`}
                            />
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCampaign(campaign);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCampaign(campaign);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Rate Cards Tab Content */}
        {activeTab === 'ratecards' && (
          <div className="flex-1 overflow-y-auto">
            {rateCardsError && (
              <div className="m-4 bg-red-50 border border-red-200 rounded-md p-3">
                <div className="text-sm text-red-700">{rateCardsError}</div>
              </div>
            )}
            {rateCardsLoading ? (
              <div className="p-6 text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600 text-sm">Loading rate cards...</p>
              </div>
            ) : (
              <RateCardList
                rateCards={rateCards}
                onEdit={handleEditRateCard}
                onDelete={handleDeleteRateCard}
                onCreate={handleCreateNewRateCard}
                loading={false}
              />
            )}
          </div>
        )}
      </div>

      {/* Right Panel - Form + Live Preview or Selected Campaign */}
      <div className="flex-1 flex flex-col">
        {activeTab === 'ratecards' && isRateCardFormOpen ? (
          <div className="flex-1 p-6 w-full">
            <RateCardForm
              rateCard={editingRateCard}
              isOpen={isRateCardFormOpen}
              onClose={handleCloseRateCardForm}
              onSubmit={editingRateCard ? handleUpdateRateCard : handleCreateRateCard}
              loading={rateCardFormLoading}
              inline
            />
          </div>
        ) : isFormOpen ? (
          <div className="flex-1 p-6 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
              {/* Inline Form */}
              <div className="w-full">
                <CampaignForm
                  campaign={editingCampaign}
                  isOpen={isFormOpen}
                  onClose={handleCloseForm}
                  onSubmit={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
                  loading={formLoading}
                  onChange={handleDraftChange}
                  inline
                  rateCards={rateCards}
                />

                {/* Selected Profiles below the form */}
                <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Selected Profiles</h3>
                    {selectedProfiles.length > 0 && (
                      <span className="text-sm text-gray-500">{selectedProfiles.length}</span>
                    )}
                  </div>
                  {selectedProfiles.length === 0 ? (
                    <div className="text-sm text-gray-500">No profiles selected yet. Select from the preview on the right.</div>
                  ) : (
                    <div className="space-y-3">
                      {selectedProfiles.map(sp => {
                        const full = previewProfiles.find(pp => pp.id === sp.profileId);
                        const name = full?.candidateName || sp.profileId;
                        const subtitle = full?.title || '';
                        const selectedRole = selectedRateCard?.roles.find(r => r.id === sp.selectedRoleId);
                        const campaignCurrency = (draftCampaign?.currency || selectedCampaign?.currency || 'USD');
                        const convertedRate = selectedRole ? convertedRates[selectedRole.id] : 0;

                        return (
                          <div key={sp.profileId} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{name}</div>
                              {subtitle && (
                                <div className="text-xs text-gray-500">{subtitle}</div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <select
                                value={sp.selectedRoleId || ''}
                                onChange={(e) => setSelectedProfiles(prev =>
                                  prev.map(x =>
                                    x.profileId === sp.profileId
                                      ? { ...x, selectedRoleId: e.target.value }
                                      : x
                                  )
                                )}
                                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="">Select Role</option>
                                {selectedRateCard?.roles.map(role => (
                                  <option key={role.id} value={role.id}>
                                    {role.title}
                                  </option>
                                ))}
                              </select>
                              {selectedRole && (
                                <span className="text-sm text-gray-600">
                                  {getCurrencySymbol(campaignCurrency)} {convertedRate?.toLocaleString() || '0'}/day
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setSelectedProfiles(prev => prev.filter(x => x.profileId !== sp.profileId))}
                                className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Live Profiles Preview */}
              <div className="w-full">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Preview Profiles</h3>
                    {previewLoading && (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-500" />
                    )}
                  </div>
                  <div className="space-y-4">
                    {previewProfiles.length === 0 && !previewLoading && (
                      <div className="text-sm text-gray-500">No profiles match the current filters.</div>
                    )}
                    {previewProfiles.filter(p => !selectedProfiles.some(sp => sp.profileId === p.id)).map((p) => {
                      const isSelected = selectedProfiles.some(sp => sp.profileId === p.id);
                      const campaignCurrency = (draftCampaign?.currency || selectedCampaign?.currency || 'USD');
                      const converted = convertedRates.base || 0;
                      return (
                        <div key={p.id} className="border border-gray-200 rounded-md">
                          <div className="p-4">
                            <StaffProfileCard
                              id={p.id}
                              candidateName={p.candidateName || 'Unknown'}
                              title={p.title}
                              experience={p.experience}
                              verification={p.verification}
                              skills={p.skills}
                              industry={p.industry}
                              industries={p.industries}
                              summary={p.summary}
                              email={p.email}
                              phone={p.phone}
                              linkedInLink={p.linkedInLink}
                              dayRate={converted}
                              dayRateCurrency={campaignCurrency}
                              onBookmark={() => { }}
                              isBookmarked={false}
                              profileData={p}
                              hideShortlistButton
                            />
                          </div>
                          <div className="px-4 pb-4">
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => toggleSelectProfile(p)}
                                className={`px-3 py-2 rounded-md text-sm font-medium ${isSelected ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-blue-600 text-white hover:bg-blue-700'} transition-colors`}
                              >
                                {isSelected ? 'Remove' : 'Select'}
                              </button>
                              {isSelected && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-600">
                                    {getCurrencySymbol(campaignCurrency)} {converted?.toLocaleString() || '0'}/day
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : selectedCampaign ? (
          <div className="flex-1 p-6 w-full">
            <div className="w-full">
              <div className="mb-6">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCampaign.title}</h2>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedCampaign.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                      }`}
                  >
                    {selectedCampaign.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-gray-600 text-lg">{selectedCampaign.description}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Campaign Settings */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Settings</h3>

                  <div className="space-y-4">
                    {/* Multiplier removed */}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Currency
                      </label>
                      <div className="text-sm text-gray-900">
                        {selectedCampaign.currency || 'Not set'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <div className="text-sm text-gray-600">
                        {selectedCampaign.isActive ? 'Active campaign' : 'Inactive campaign'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Created
                      </label>
                      <div className="text-sm text-gray-600">
                        {new Date(selectedCampaign.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {selectedCampaign.updatedAt !== selectedCampaign.createdAt && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Updated
                        </label>
                        <div className="text-sm text-gray-600">
                          {new Date(selectedCampaign.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Filters hidden for now */}

                {/* Selected Profiles */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Profiles</h3>
                  {selectedProfiles.length === 0 ? (
                    <div className="text-sm text-gray-500">No profiles selected</div>
                  ) : (
                    <div className="space-y-3">
                      {selectedProfiles.map(sp => (
                        <div key={sp.profileId} className="flex items-center justify-between">
                          <div className="text-sm text-gray-700">{sp.profileId}</div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{draftCampaign?.currency || 'USD'}</span>
                            <input
                              type="number"
                              value={0}
                              onChange={(e) => updateSelectedDayRate(sp.profileId, parseFloat(e.target.value) || 0)}
                              className="w-32 px-3 py-2 border border-gray-300 rounded-md text-right text-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 mb-4">
                <FontAwesomeIcon icon={faEdit} className="text-6xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Campaign</h3>
              <p className="text-gray-600">
                Choose a campaign from the left panel to view its details
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal removed: form only renders inline above with live preview */}
    </div>
  );
};

export default AdminPage;
